import { type ChatMessageItem } from '../../components/chat/ChatMessage/types';
import { type SessionLogDetails, type SessionTimelineEntry } from '../../services/api/sessions';
import { type SuggestedAction } from '../../types/ai';

// Sessioner före 2026-09-29 sparade bara hjärnans val, inte det kunden fick.
// Orderkortets knappar och "Visa fler" saknas därför i gamla sessioner.
export const shownChips = (entry: SessionTimelineEntry): SuggestedAction[] => {
  const debug = entry.metadata?._debug;
  return debug?.shownActions ?? debug?.suggestedActions ?? [];
};

export const toCustomerMessage = (
  entry: SessionTimelineEntry,
  language?: string,
): ChatMessageItem | null => {
  // Bara assistentens svar: kundens egen text ritas som ren text i admin, där
  // markdown skulle ladda bilder från adresser kunden själv skrivit.
  if (entry.role !== 'assistant') return null;
  return {
    id: `turn-${entry.sequence}`,
    role: entry.role,
    content: entry.content,
    createdAt: entry.createdAt,
    ...(language ? { detectedLanguage: language } : {}),
  };
};

export type SessionContextRow = { label: string; value: string; tone?: 'warn' | 'ok' };

export const describeSessionContext = (details: SessionLogDetails): SessionContextRow[] => {
  const rows: SessionContextRow[] = [];
  const state = details.state ?? ({} as SessionLogDetails['state']);
  const site = (state.metadata as { siteContext?: Record<string, unknown> } | undefined)
    ?.siteContext;
  const hostname = typeof site?.hostname === 'string' ? site.hostname : undefined;
  const pathname = typeof site?.pathname === 'string' ? site.pathname : undefined;
  const pageType = typeof site?.pageType === 'string' ? site.pageType : undefined;
  if (hostname) rows.push({ label: 'Marknad', value: hostname });
  if (pathname) {
    rows.push({ label: 'Sida', value: pageType ? `${pathname} (${pageType})` : pathname });
  }

  if (state.language || state.uiLanguage) {
    rows.push({
      label: 'Språk',
      value: [state.language, state.uiLanguage && `UI ${state.uiLanguage}`].filter(Boolean).join(' · '),
    });
  }

  const flags = details.sessionFlags;
  if (flags?.handoffStatus) {
    rows.push({
      label: 'Handoff',
      value: [flags.handoffStatus, flags.handoffViaMail ? 'via mejl' : null].filter(Boolean).join(' · '),
      tone: 'warn',
    });
  }
  if (flags?.emailDraftStatus) {
    rows.push({ label: 'Mejlutkast', value: flags.emailDraftStatus });
  }

  if (state.status === 'ended') {
    rows.push({
      label: 'Avslutad',
      value:
        [state.endedAt && new Date(state.endedAt).toLocaleString('sv-SE'), state.endReason]
          .filter(Boolean)
          .join(' · ') || 'ja',
    });
  }
  return rows;
};

// Hjärnans text och plattformens fasta texter (mejlfrågan, grinden,
// returflödet) såg likadana ut i admin — båda hette "llm".
export const routingSourceLabel = (src?: string): string => {
  if (!src) return '';
  if (src === 'brain_loop') return 'hjärnan';
  if (src === 'brain_loop_email_draft') return 'hjärnan · mejlutkast';
  if (src === 'fast_authoritative') return 'fast';
  if (src === 'fast_fallback') return 'fast↓';
  if (src === 'llm_disambiguation') return 'llm+retry';
  if (src === 'llm') return 'llm';
  return `fast text · ${src}`;
};

export const speakerLabel = (entry: SessionTimelineEntry): string =>
  entry.fromAgent ? ['Kundtjänst', entry.senderAlias].filter(Boolean).join(' · ') : entry.role;
